/**
 * Modul "Familieninformationen": drei strikt getrennte Kategorien
 * ("Mafien/ Clans", "Gangs", "Biker & Clubs"), jede als eigene Spalte mit
 * eigener Kartenliste - vollständig neu aufgebaut seit Migration 0042
 * (ersetzt die vorherige, unkategorisierte Einzelliste).
 *
 * Eine Familie gehört fest zu der Kategorie, in der sie über "+ Familie
 * hinzufügen" angelegt wurde - die Kategorie ist beim Bearbeiten bewusst
 * KEIN änderbares Feld (siehe FamilyForm), damit Familien niemals
 * versehentlich in eine andere Spalte wandern.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import type { FamilyInfo, FamilyInfoCategory } from "../../types";

const CATEGORIES: { key: FamilyInfoCategory; label: string }[] = [
  { key: "mafia", label: "Mafien/ Clans" },
  { key: "gang", label: "Gangs" },
  { key: "biker", label: "Biker & Clubs" },
];

export default function FamilyInfoPage() {
  const canCreate = usePermission("familyinfo.create");
  const canEdit = usePermission("familyinfo.edit");
  const canDelete = usePermission("familyinfo.delete");

  const [families, setFamilies] = useState<FamilyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ category: FamilyInfoCategory; family: FamilyInfo | null } | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFamilies(await api.get<FamilyInfo[]>("/api/family-info"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(f: FamilyInfo) {
    if (!confirm(`Möchtest du "${f.family_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/family-info/${f.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Familieninformationen</h1>
        <p className="text-sm text-gray-500 mt-1">Verwaltung von Familien innerhalb des GTA-Roleplay-Servers, unterteilt nach Kategorie.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        {CATEGORIES.map((cat) => {
          const items = families.filter((f) => f.category === cat.key);
          return (
            <Card key={cat.key}>
              {/* Bewusst kein Card­Header (dessen "text-base"-Titel wird
                  systemweit für alle anderen Karten genutzt) - hier eine
                  eigene, etwas größere Überschrift ("text-lg" statt
                  "text-base"), ausschließlich für die drei Kategorie-
                  Spalten dieser Seite. */}
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-100">{cat.label}</h3>
                {canCreate && (
                  <button className="btn-primary whitespace-nowrap" onClick={() => setEditing({ category: cat.key, family: null })}>
                    + Familie hinzufügen
                  </button>
                )}
              </div>
              {loading ? <Spinner /> : items.length === 0 ? (
                <EmptyState message="Keine Familien erfasst." />
              ) : (
                <div className="space-y-3">
                  {items.map((f) => (
                    <div key={f.id} className="bg-surface-raised rounded-lg p-3">
                      <p className="text-sm text-gray-100"><span className="text-gray-500">Name:</span> <span className="font-medium">{f.family_name}</span></p>
                      <p className="text-sm text-gray-300 mt-1"><span className="text-gray-500">PLZ:</span> {f.postal_code || "—"}</p>
                      <p className="text-sm text-gray-300 mt-1 whitespace-pre-wrap"><span className="text-gray-500">Notizen:</span> {f.notes || "—"}</p>
                      {(canEdit || canDelete) && (
                        <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-surface-border/60">
                          {canEdit && (
                            <button className="text-accent text-xs hover:underline" onClick={() => setEditing({ category: cat.key, family: f })}>
                              Bearbeiten
                            </button>
                          )}
                          {canDelete && (
                            <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(f)}>
                              Löschen
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {editing !== undefined && (
        <FamilyForm
          category={editing.category}
          categoryLabel={CATEGORIES.find((c) => c.key === editing.category)!.label}
          family={editing.family}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}
    </div>
  );
}

function FamilyForm({ category, categoryLabel, family, onClose, onSaved }: {
  category: FamilyInfoCategory; categoryLabel: string; family: FamilyInfo | null; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!family;
  const [familyName, setFamilyName] = useState(family?.family_name ?? "");
  const [postalCode, setPostalCode] = useState(family?.postal_code ?? "");
  const [notes, setNotes] = useState(family?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit) await api.put(`/api/family-info/${family!.id}`, { familyName, postalCode, notes });
      else await api.post("/api/family-info", { category, familyName, postalCode, notes });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? `Familie bearbeiten (${categoryLabel})` : `Familie hinzufügen (${categoryLabel})`}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="family-info-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="family-info-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Name *</label>
          <input className="input" value={familyName} onChange={(e) => setFamilyName(e.target.value)} required autoFocus />
        </div>
        <div>
          <label className="label">PLZ</label>
          <input className="input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="z.B. 12345" />
        </div>
        <div>
          <label className="label">Notizen</label>
          <textarea className="input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}
